import React, { useCallback, useRef, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';

/**
 * A confirmation step for actions that are hard to undo. Say what will happen in plain words and
 * name the thing affected; the confirm button repeats the action ("Approve domain", not "OK").
 */
const ConfirmDialog = ({
  show,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  busy = false,
  onConfirm,
  onCancel,
}) => (
  <Modal show={show} onHide={busy ? undefined : onCancel} centered>
    <Modal.Header closeButton={!busy}>
      <Modal.Title as="h2" className="h5 mb-0">{title}</Modal.Title>
    </Modal.Header>
    <Modal.Body>{children}</Modal.Body>
    <Modal.Footer>
      <Button variant="outline-secondary" onClick={onCancel} disabled={busy}>{cancelLabel}</Button>
      <Button variant={variant} onClick={onConfirm} disabled={busy} autoFocus>
        {busy && <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />}
        {confirmLabel}
      </Button>
    </Modal.Footer>
  </Modal>
);

/**
 * Promise-based confirmation, a drop-in for window.confirm:
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   if (!(await confirm({ title: 'Remove example.com?', body: '...', confirmLabel: 'Remove', variant: 'danger' }))) return;
 *   ...
 *   return <>{confirmDialog}...</>;
 */
export const useConfirm = () => {
  const [options, setOptions] = useState(null);
  const resolver = useRef(null);

  const settle = useCallback((result) => {
    resolver.current?.(result);
    resolver.current = null;
    setOptions(null);
  }, []);

  const confirm = useCallback((next) => new Promise((resolve) => {
    resolver.current?.(false);
    resolver.current = resolve;
    setOptions(next);
  }), []);

  const confirmDialog = (
    <ConfirmDialog
      show={!!options}
      title={options?.title}
      confirmLabel={options?.confirmLabel}
      cancelLabel={options?.cancelLabel}
      variant={options?.variant}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    >
      {typeof options?.body === 'string' ? <p className="mb-0">{options.body}</p> : options?.body}
    </ConfirmDialog>
  );

  return { confirm, confirmDialog };
};

export default ConfirmDialog;
